/**
 * Provider Categories Hooks
 * Hooks for fetching and managing provider categories
 */

import { useQuery } from '@tanstack/react-query';
import api from '../lib/api';

// Types
export interface CategoryMetadata {
  code: string;
  displayName: string;
  description: string;
  icon: string;
  color: string;
  displayOrder: number;
}

export interface CategoryWithCount extends CategoryMetadata {
  templateCount: number;
}

export interface CategoryStats {
  totalCategories: number;
  categoriesWithTemplates: number;
  totalTemplates: number;
  categoryBreakdown: CategoryWithCount[];
}

/**
 * Fetch all provider categories with template counts
 * @param includeEmpty - Include categories with no templates (default: true)
 */
export function useProviderCategories(includeEmpty: boolean = true) {
  return useQuery<CategoryWithCount[]>({
    queryKey: ['provider-categories', includeEmpty],
    queryFn: async () => {
      const response = await api.get(`/provider-engine/categories?includeEmpty=${includeEmpty}`);
      return response.data;
    },
  });
}

/**
 * Fetch categories that have at least one template
 */
export function useCategoriesWithTemplates() {
  return useProviderCategories(false);
}

/**
 * Fetch metadata for a specific category
 * @param code - Category code (e.g., 'BAAS', 'EXCHANGE')
 */
export function useCategoryMetadata(code: string | null) {
  return useQuery<CategoryMetadata>({
    queryKey: ['provider-category', code],
    queryFn: async () => {
      const response = await api.get(`/provider-engine/categories/${code}`);
      return response.data;
    },
    enabled: !!code,
  });
}

/**
 * Fetch category statistics
 */
export function useCategoryStats() {
  return useQuery<CategoryStats>({
    queryKey: ['provider-category-stats'],
    queryFn: async () => {
      const response = await api.get('/provider-engine/categories/stats');
      return response.data;
    },
  });
}

/**
 * Fetch template count for a specific category
 * @param code - Category code
 */
export function useCategoryTemplateCount(code: string | null) {
  return useQuery<{ count: number }>({
    queryKey: ['provider-category-count', code],
    queryFn: async () => {
      const response = await api.get(`/provider-engine/categories/${code}/count`);
      return response.data;
    },
    enabled: !!code,
  });
}

// Icon mapping for Lucide icons
export const CATEGORY_ICON_MAP: Record<string, string> = {
  Building2: 'Building2',
  ArrowLeftRight: 'ArrowLeftRight',
  Shuffle: 'Shuffle',
  LineChart: 'LineChart',
  Blocks: 'Blocks',
  Coins: 'Coins',
  Bitcoin: 'Bitcoin',
  CreditCard: 'CreditCard',
  Gift: 'Gift',
  UserCheck: 'UserCheck',
  Bell: 'Bell',
  Building: 'Building',
  ShoppingCart: 'ShoppingCart',
  Calculator: 'Calculator',
  FileText: 'FileText',
};
